import { useCallback, useEffect, useState } from 'react';
import {
  addDoc,
  collectionGroup,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  runTransaction,
  setDoc,
  Timestamp,
  where,
} from 'firebase/firestore';
import { auth, db } from '../../../config/firebase';
import {
  publicTeamDirectoryDoc,
  teamDoc,
  teamInviteDoc,
  teamInvitesCollection,
  teamRequestsCollection,
  teamSlugDoc,
  userDoc,
} from '../../../firebase/collections';
import { useAuth } from '../../../hooks/useAuth';
import {
  deriveAdminUids,
  deriveMemberUids,
  MAX_TEAM_MEMBERS,
  type Team,
  type TeamInvite,
  type TeamMember,
} from '../types';
import { readMemberProfile, readPublicPlayerByUid } from '../utils/memberProfile';

export interface PendingInviteWithTeam extends TeamInvite {
  teamName: string;
  teamAvatarUrl: string | null;
  inviterName: string;
}

export type SentInviteRow = TeamInvite & {
  inviteeName: string;
  inviteeSlug?: string;
  inviteeAvatarUrl?: string | null;
};

async function leaveOldTeamInTransaction(
  transaction: Parameters<Parameters<typeof runTransaction>[1]>[0],
  oldTeamId: string,
  uid: string,
  now: Timestamp
): Promise<void> {
  const oldTeamRef = teamDoc(oldTeamId);
  const oldTeamSnap = await transaction.get(oldTeamRef);
  if (!oldTeamSnap.exists()) return;
  const oldTeamData = oldTeamSnap.data() as Team;
  if (oldTeamData.ownerId === uid) {
    if (oldTeamData.members.length > 1) {
      throw new Error('Transfer ownership of your current team before joining a new one.');
    }
    transaction.delete(oldTeamRef);
    transaction.delete(teamSlugDoc(oldTeamData.slug));
    transaction.delete(publicTeamDirectoryDoc(oldTeamData.slug));
    return;
  }
  const remaining = oldTeamData.members.filter((m) => m.uid !== uid);
  transaction.set(
    oldTeamRef,
    {
      members: remaining,
      memberUids: deriveMemberUids(remaining),
      adminUids: deriveAdminUids(remaining),
      updatedAt: now,
    },
    { merge: true }
  );
  transaction.set(
    publicTeamDirectoryDoc(oldTeamData.slug),
    {
      memberCount: remaining.length,
      updatedAt: now,
    },
    { merge: true }
  );
}

export function useTeamInvites() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const clearError = useCallback(() => setError(null), []);

  const sendInvite = useCallback(
    async (
      teamId: string,
      inviteeUid: string,
      inviteeProfile?: { displayName?: string; slug?: string; avatarUrl?: string | null }
    ): Promise<void> => {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('Not signed in.');

      setLoading(true);
      setError(null);

      try {
        const uid = currentUser.uid;
        const now = Timestamp.now();
        const teamSnap = await getDoc(teamDoc(teamId));
        if (!teamSnap.exists()) throw new Error('Team not found.');
        const teamData = teamSnap.data() as Team;

        const callerMember = teamData.members.find((m) => m.uid === uid);
        if (!callerMember || (callerMember.role !== 'owner' && callerMember.role !== 'admin')) {
          throw new Error('Only the team owner or admins can send invites.');
        }
        if (teamData.members.length >= MAX_TEAM_MEMBERS) {
          throw new Error(`Team is full (max ${MAX_TEAM_MEMBERS} members).`);
        }
        if (teamData.members.some((m) => m.uid === inviteeUid)) {
          throw new Error('Player is already a member.');
        }

        const existingInvites = await getDocs(
          query(
            teamInvitesCollection(teamId),
            where('inviteeId', '==', inviteeUid),
            where('status', '==', 'pending')
          )
        );
        if (!existingInvites.empty) throw new Error('An invite is already pending for this player.');

        const existingRequests = await getDocs(
          query(
            teamRequestsCollection(teamId),
            where('requesterId', '==', inviteeUid),
            where('status', '==', 'pending')
          )
        );
        if (!existingRequests.empty) {
          throw new Error('This player already has a pending join request. Accept it instead.');
        }

        const resolved =
          inviteeProfile?.slug || inviteeProfile?.displayName
            ? inviteeProfile
            : ((await readPublicPlayerByUid(inviteeUid)) ?? undefined);

        await addDoc(teamInvitesCollection(teamId), {
          teamId,
          inviteeId: inviteeUid,
          inviterId: uid,
          status: 'pending',
          createdAt: now,
          respondedAt: null,
          ...(resolved?.displayName ? { inviteeName: resolved.displayName } : {}),
          ...(resolved?.slug ? { inviteeSlug: resolved.slug } : {}),
          ...(resolved?.avatarUrl !== undefined
            ? { inviteeAvatarUrl: resolved.avatarUrl ?? null }
            : {}),
        });
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to send invite.';
        setError(message);
        throw new Error(message);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const acceptInvite = useCallback(
    async (teamId: string, inviteId: string, shareCollection: boolean): Promise<void> => {
      const currentUser = auth.currentUser;
      if (!currentUser) throw new Error('Not signed in.');

      setLoading(true);
      setError(null);

      try {
        const uid = currentUser.uid;
        const now = Timestamp.now();
        const tRef = teamDoc(teamId);
        const inviteRef = teamInviteDoc(teamId, inviteId);
        const userRef = userDoc(uid);

        let joinedVisibility: 'public' | 'team' | 'private' = 'private';
        let joinedShare = false;
        let joinedProfile: { displayName?: string; slug?: string } = {};
        let joinedRevision: number | undefined;

        await runTransaction(db, async (transaction) => {
          const inviteSnap = await transaction.get(inviteRef);
          if (!inviteSnap.exists()) throw new Error('Invite not found.');
          const invite = inviteSnap.data() as TeamInvite;
          if (invite.inviteeId !== uid) throw new Error('This invite is not for you.');
          if (invite.status !== 'pending') throw new Error('Invite is no longer pending.');

          const teamSnap = await transaction.get(tRef);
          if (!teamSnap.exists()) throw new Error('Team not found.');
          const teamData = teamSnap.data() as Team;

          if (teamData.members.length >= MAX_TEAM_MEMBERS) throw new Error('Team is full.');
          if (teamData.members.some((m) => m.uid === uid)) throw new Error('Already a member.');

          const userSnap = await transaction.get(userRef);
          const userData = userSnap.exists() ? userSnap.data() : {};

          if (userData?.teamId && userData.teamId !== teamId) {
            await leaveOldTeamInTransaction(transaction, userData.teamId as string, uid, now);
          }

          const profile = await readMemberProfile(
            transaction,
            uid,
            userData,
            currentUser.displayName
          );

          const existingVisibility =
            userData?.collectionVisibility === 'public' ||
            userData?.collectionVisibility === 'team' ||
            userData?.collectionVisibility === 'private'
              ? (userData.collectionVisibility as 'public' | 'team' | 'private')
              : userData?.allowDirectoryListing === true
                ? 'public'
                : 'private';
          // Public/team collection visibility always shares with the team.
          const effectiveShare =
            shareCollection ||
            existingVisibility === 'public' ||
            existingVisibility === 'team';

          const newMember: TeamMember = {
            uid,
            role: 'member',
            shareCollection: effectiveShare,
            joinedAt: now,
            ...profile,
          };
          const updatedMembers = [...teamData.members, newMember];

          transaction.set(
            tRef,
            {
              members: updatedMembers,
              memberUids: deriveMemberUids(updatedMembers),
              adminUids: deriveAdminUids(updatedMembers),
              updatedAt: now,
            },
            { merge: true }
          );
          transaction.set(
            publicTeamDirectoryDoc(teamData.slug),
            { memberCount: updatedMembers.length, updatedAt: now },
            { merge: true }
          );
          transaction.set(inviteRef, { status: 'accepted', respondedAt: now }, { merge: true });
          const userPatch: Record<string, unknown> = { teamId, updatedAt: now };
          // Sharing with team requires at least team collection visibility.
          const nextVisibility =
            effectiveShare && existingVisibility === 'private' ? 'team' : existingVisibility;
          if (nextVisibility !== existingVisibility) {
            userPatch.collectionVisibility = nextVisibility;
          }
          transaction.set(userRef, userPatch, { merge: true });

          joinedVisibility = nextVisibility;
          joinedShare = effectiveShare;
          // Already in hand from the user document read above. Projecting
          // without it leaves the doc permanently "stale" to the heal check.
          const revision = (userData?.collectionSummary as { revision?: unknown } | undefined)
            ?.revision;
          joinedRevision = typeof revision === 'number' ? revision : undefined;
          joinedProfile = {
            displayName: profile.displayName,
            slug: profile.slug,
          };
        });

        if (joinedShare) {
          try {
            const {
              loadUserCollectionMap,
              syncTeamSharedCollection,
            } = await import('../utils/teamSharedCollectionSync');
            const entries = await loadUserCollectionMap(uid);
            await syncTeamSharedCollection({
              teamId,
              uid,
              displayName: joinedProfile.displayName,
              slug: joinedProfile.slug,
              entries,
              visibility: joinedVisibility,
              shareCollection: true,
              revision: joinedRevision,
            });
          } catch (err) {
            console.warn('[useTeamInvites] team collection seed after accept failed', err);
          }
        }

        const pendingRequests = await getDocs(
          query(
            teamRequestsCollection(teamId),
            where('requesterId', '==', uid),
            where('status', '==', 'pending')
          )
        );
        for (const reqDoc of pendingRequests.docs) {
          await setDoc(
            reqDoc.ref,
            { status: 'cancelled', respondedAt: Timestamp.now() },
            { merge: true }
          );
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to accept invite.';
        setError(message);
        throw new Error(message);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const declineInvite = useCallback(async (teamId: string, inviteId: string): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      await setDoc(
        teamInviteDoc(teamId, inviteId),
        { status: 'declined', respondedAt: Timestamp.now() },
        { merge: true }
      );
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to decline invite.';
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  const revokeInvite = useCallback(async (teamId: string, inviteId: string): Promise<void> => {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error('Not signed in.');

    setLoading(true);
    setError(null);

    try {
      const teamSnap = await getDoc(teamDoc(teamId));
      if (!teamSnap.exists()) throw new Error('Team not found.');
      const teamData = teamSnap.data() as Team;
      const callerMember = teamData.members.find((m) => m.uid === currentUser.uid);
      if (!callerMember || (callerMember.role !== 'owner' && callerMember.role !== 'admin')) {
        throw new Error('Only the team owner or admins can revoke invites.');
      }
      await setDoc(
        teamInviteDoc(teamId, inviteId),
        { status: 'revoked', respondedAt: Timestamp.now() },
        { merge: true }
      );
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to revoke invite.';
      setError(message);
      throw new Error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  return { sendInvite, acceptInvite, declineInvite, revokeInvite, loading, error, clearError };
}

export function usePendingInvites() {
  const { user } = useAuth();
  const [invites, setInvites] = useState<PendingInviteWithTeam[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setInvites([]);
      setLoading(false);
      return;
    }

    const q = query(
      collectionGroup(db, 'invites'),
      where('inviteeId', '==', user.uid),
      where('status', '==', 'pending')
    );

    const unsub = onSnapshot(
      q,
      async (snap) => {
        const rows: PendingInviteWithTeam[] = [];
        for (const d of snap.docs) {
          const data = d.data() as TeamInvite;
          let teamName = 'Team';
          let teamAvatarUrl: string | null = null;
          let inviterName = 'Someone';

          try {
            const teamSnap = await getDoc(teamDoc(data.teamId));
            if (teamSnap.exists()) {
              const team = teamSnap.data() as Team;
              teamName = team.name;
              teamAvatarUrl = team.avatarUrl;
              const inviter = team.members.find((m) => m.uid === data.inviterId);
              if (inviter?.displayName) inviterName = inviter.displayName;
            }
            if (inviterName === 'Someone') {
              const inviterProfile = await readPublicPlayerByUid(data.inviterId);
              if (inviterProfile?.displayName) {
                inviterName = inviterProfile.displayName;
              }
            }
          } catch {
            // ignore enrichment failures
          }

          rows.push({
            ...data,
            id: d.id,
            teamName,
            teamAvatarUrl,
            inviterName,
          });
        }
        setInvites(rows);
        setLoading(false);
      },
      () => {
        setInvites([]);
        setLoading(false);
      }
    );

    return unsub;
  }, [user?.uid]);

  return { invites, loading };
}

export function useSentInvites(teamId: string | null | undefined) {
  const [invites, setInvites] = useState<SentInviteRow[]>([]);
  const [loading, setLoading] = useState(Boolean(teamId));

  useEffect(() => {
    if (!teamId) {
      setInvites([]);
      setLoading(false);
      return;
    }

    const q = query(teamInvitesCollection(teamId), where('status', '==', 'pending'));
    const unsub = onSnapshot(
      q,
      async (snap) => {
        const rows = await Promise.all(
          snap.docs.map(async (d) => {
            const raw = d.data() as TeamInvite & {
              inviteeName?: string;
              inviteeSlug?: string;
              inviteeAvatarUrl?: string | null;
            };
            const data = { ...raw, id: d.id };

            // Prefer denormalized fields written at invite time; otherwise resolve publicly.
            let inviteeName =
              typeof raw.inviteeName === 'string' && raw.inviteeName.trim()
                ? raw.inviteeName.trim()
                : '';
            let inviteeSlug =
              typeof raw.inviteeSlug === 'string' && raw.inviteeSlug ? raw.inviteeSlug : undefined;
            let inviteeAvatarUrl =
              typeof raw.inviteeAvatarUrl === 'string' ? raw.inviteeAvatarUrl : null;

            if (!inviteeName || !inviteeSlug) {
              const profile = await readPublicPlayerByUid(data.inviteeId);
              if (profile) {
                inviteeName = inviteeName || profile.displayName;
                inviteeSlug = inviteeSlug || profile.slug;
                inviteeAvatarUrl = inviteeAvatarUrl ?? profile.avatarUrl;
              }
            }

            return {
              ...data,
              inviteeName: inviteeName || 'Player',
              inviteeSlug,
              inviteeAvatarUrl,
            };
          })
        );
        setInvites(rows);
        setLoading(false);
      },
      () => {
        setInvites([]);
        setLoading(false);
      }
    );
    return unsub;
  }, [teamId]);

  return { invites, loading };
}
